/* eslint-disable max-len */
import { Entity,  Runner, SystemInterface } from '@goodboydigital/odie';
import p2 from 'p2';
import { Ticker } from 'pixi.js';

import { Settings } from '../../AppConfig';
import { Random } from '../../utils/Random';
import { Timer } from '../../utils/Timer';
import { BaseEntity } from '../entities/BaseEntity';
import { Game } from '../Game';
import { PhysicsComponent } from './PhysicsComponent';
import { PhysicsContact } from './PhysicsContact';
import { PhysicsPart } from './PhysicsUtils';

export class PhysicsSystem implements SystemInterface
{
    public game: Game;
    public world: p2.World;
    public entities: BaseEntity[];
    public physicsFixedUpdateRunner: Runner;
    public physicsFreeUpdateRunner: Runner;
    public timer: Timer;
    public contact: PhysicsContact;
    public useCulling = false;

    private partsScheduledToAdd: PhysicsPart[] = [];
    private partsScheduledToRemove: PhysicsPart[] = [];

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.timer = new Timer();
        this.useCulling = Settings.physicsCulling;

        this.world = new p2.World({
            gravity: [0, -800],
        });
        this.world.defaultContactMaterial.friction = 0.01;

        const solver = this.world.solver as p2.GSSolver;

        solver.tolerance = 0.1;
        solver.iterations = 2;

        this.contact = new PhysicsContact(this.world);
        // this.createContactMaterials();

        this.world.on('postStep', this.handleWorldPostStep.bind(this));
        this.world.on('beginContact', this.handleBeginContact.bind(this));
        this.world.on('endContact', this.handleEndContact.bind(this));

        this.entities = [];
        this.physicsFixedUpdateRunner = new Runner('physicsFixedUpdate', 1);
        this.physicsFreeUpdateRunner = new Runner('physicsFreeUpdate', 1);

        const fps = Settings.fps;

        if (fps === 0)
        {
            // Simulate framerate oscilation
            setInterval(() => { Ticker.system.maxFPS = 10 + Math.round(Random.range(0, 50)); }, 500);
        }
        else
        {
            // Max frame rate
            Ticker.system.maxFPS = Number(fps);

            // Force max 30fps if stage has been set to halve fps
            if (this.game.app.stage.useHalfFPS)
            {
                this.game.app.stage.useHalfFPS = false;
                Ticker.system.maxFPS = 30;
            }
        }

        this.game.onIdle.connect(this.reset.bind(this));
        this.game.onPlay.connect(this.reset.bind(this));
    }

    public reset():void
    {
        this.timer.clear();
    }

    public entityAddedToScene(entity: Entity): void
    {
        const en = entity as BaseEntity;
        const physicsComp = entity.getComponent(PhysicsComponent);

        if (!physicsComp || this.entities.includes(en)) return;
        this.entities.push(en);
        physicsComp.system = this;

        if (!physicsComp.culling || !this.useCulling)
        {
            for (const f in physicsComp.bodies) this.world.addBody(physicsComp.bodies[f]);
            for (const f in physicsComp.springs) this.world.addSpring(physicsComp.springs[f]);
            for (const f in physicsComp.constraints) this.world.addConstraint(physicsComp.constraints[f]);
        }
    }

    public entityRemovedFromScene(entity: Entity): void
    {
        const en = entity as BaseEntity;

        if (!this.entities.includes(en)) return;
        this.entities.splice(this.entities.indexOf(en), 1);

        const physicsComp = entity.getComponent(PhysicsComponent);

        for (const i in physicsComp.bodies) this.world.removeBody(physicsComp.bodies[i]);
        for (const i in physicsComp.springs) this.world.removeSpring(physicsComp.springs[i]);
        for (const i in physicsComp.constraints) this.world.removeConstraint(physicsComp.constraints[i]);
    }

    public get gravity(): number
    {
        return this.world.gravity[1];
    }

    public set gravity(v: number)
    {
        this.world.gravity[1] = v;
    }

    // === ADD/REMOVE PARTS (BODY/CONSTRAINT/SPRING) ==================================================================

    /**
     * Schedule parts to be added, can be safely called under ongoing physics step
     */
    public safeAddParts(...list: PhysicsPart[]): void
    {
        for (const part of list)
        {
            if (this.partsScheduledToRemove.includes(part))
            {
                this.partsScheduledToRemove.splice(this.partsScheduledToRemove.indexOf(part), 1);
            }
            if (!this.partsScheduledToAdd.includes(part))
            {
                this.partsScheduledToAdd.push(part);
            }
        }
    }

    /**
     * Schedule parts to be removed, can be safely called under ongoing physics step
     */
    public safeRemoveParts(...list: PhysicsPart[]): void
    {
        for (const part of list)
        {
            if (this.partsScheduledToAdd.includes(part))
            {
                this.partsScheduledToAdd.splice(this.partsScheduledToAdd.indexOf(part), 1);
            }
            if (!this.partsScheduledToRemove.includes(part))
            {
                this.partsScheduledToRemove.push(part);
            }
        }
    }

    /**
     * Effectivelly add parts scheduled to the physics world
     */
    private addScheduledParts(): void
    {
        for (const part of this.partsScheduledToAdd)
        {
            if (part instanceof p2.Constraint)
            {
                if (this.world.constraints.includes(part)) continue;
                this.world.addConstraint(part);
            }
            if (part instanceof p2.Spring)
            {
                if (this.world.springs.includes(part)) continue;
                this.world.addSpring(part);
            }
            if (part instanceof p2.Body)
            {
                if (this.world.bodies.includes(part)) continue;
                this.world.addBody(part);
            }
        }
        this.partsScheduledToAdd = [];
    }

    /**
     * Effectivelly remove parts scheduled to the physics world
     */
    private removeScheduledParts(): void
    {
        for (const part of this.partsScheduledToRemove)
        {
            if (part instanceof p2.Constraint)
            {
                if (!this.world.constraints.includes(part)) continue;
                this.world.removeConstraint(part);
            }
            if (part instanceof p2.Spring)
            {
                if (!this.world.springs.includes(part)) continue;
                this.world.removeSpring(part);
            }
            if (part instanceof p2.Body)
            {
                if (!this.world.bodies.includes(part)) continue;
                this.world.removeBody(part);
            }
        }
        this.partsScheduledToRemove = [];
    }

    /**
     * Ensure parts of a physics component to be added to the world
     */
    public ensurePartsAdded(physicsComp: PhysicsComponent): void
    {
        if (!physicsComp) return;

        for (const body of physicsComp.bodies)
        {
            if (!this.world.bodies.includes(body)) this.world.addBody(body);
        }
        for (const spring of physicsComp.springs)
        {
            if (!this.world.springs.includes(spring)) this.world.addSpring(spring);
        }

        for (const constraint of physicsComp.constraints)
        {
            if (!this.world.constraints.includes(constraint)) this.world.addConstraint(constraint);
        }
    }

    /** Ensure parts of a physics component to be removed from the world */
    public ensurePartsRemoved(physicsComp: PhysicsComponent): void
    {
        for (const body of physicsComp.bodies)
        {
            if (this.world.bodies.includes(body)) this.world.removeBody(body);
        }
        for (const spring of physicsComp.springs)
        {
            if (this.world.springs.includes(spring)) this.world.removeSpring(spring);
        }

        for (const constraint of physicsComp.constraints)
        {
            if (this.world.constraints.includes(constraint)) this.world.removeConstraint(constraint);
        }
    }

    // === UPDATE =====================================================================================================

    public preupdate(): void
    {
        /**
         * PHYSICS UPDATE NOTES
         * Considering that a constant fixed physics step is needed for reliable replays,
         * these are some of the choices we have:
         *
         * 1 - Update physics at 30 fps and game at 60 fps, interpolating sprites to physics bodies
         * That should be the best way, but the problem is that physics step 1/30 makes collision not as precise as
         * we need to be, the game does not move smooth as in 1/60 time steps
         *
         * 2 - Update both game and physics at 60 fps
         * That would be the easiest solution, but will cause a slow motion in devices that can't run the game in 60 fps
         * This is a no-go.
         *
         * 3 - Update physics at 30 fps, with 1/60 delta step and fixed 1/30 delta time and 2 max sub steps
         * In theory that would be the perfect one - physics is updated every other frame, in 1/60 delta step + 1/30 time step
         * which will make physics update twice internally, but that causes some stuttering because of the process difference
         * on each frame
         *
         * 4 - Leave to p2 to resolve that
         * A delta time can be passed to p2's update function alongside the fixed time step, and p2 will handle sub steps
         * internally. Looks good in the gameplay, but the problem is that the results seems not prroperly reproducible,
         * like we would need for replays. We can consider that if we don't need that precision.
         *
         * 5 - Double step - crazy solution
         * This is the one implemented at the moment, basically update physics twice (using 1/60 step) if framerate drops to 30.
         * Does not look correct but, surprisingly, this is providing the most smooth gameplay across different devices and framerates.
         * We might have to change this at some stage, but for now seems to be good enough to move on with the project.
         */

        if (this.game.time.frameTime > 1.9)
        {
            // Framerate dropped too much, let's update physics twice
            this.fixedUpdate();
            this.fixedUpdate();
        }
        else
        {
            // Framerate is around below 60 fps, update only once
            this.fixedUpdate();
        }

        // Always do the free update - in a separate function in case we need to change update mode
        this.freeUpdate();
    }

    /**
     * Fixed physics update - always the same delta
     */
    public fixedUpdate(): void
    {
        const fixedStep = 1 / 60;

        this.world.step(fixedStep);
        this.timer.step(fixedStep);
        this.physicsFixedUpdateRunner.emit();
        for (const en of this.entities) (en as any).physics?.physicsFixedUpdate();
    }

    /**
     * Free update where delta can vary
     * @param delta - Fluctuating delta step
     */
    public freeUpdate(step = 1): void
    {
        this.physicsFreeUpdateRunner.emit(step);
        for (const en of this.entities) (en as any).physics?.physicsFreeUpdate(step);
    }

    // === EVENT HANDLERS =============================================================================================

    private handleWorldPostStep(): void
    {
        this.removeScheduledParts();
        this.addScheduledParts();
        for (const en of this.entities) (en as any).physics?.physicsPostStepRunner.dispatch();
    }

    private handleBeginContact(e: p2.BeginContactEvent): void
    {
        const bodyA = e.bodyA;
        const bodyB = e.bodyB;

        if (bodyA && bodyB)
        {
            if (bodyA.onBeginContact) bodyA.onBeginContact(bodyB);
            if (bodyB.onBeginContact) bodyB.onBeginContact(bodyA);
            if (bodyA.physicsComponent) bodyA.physicsComponent.onBeginContact.dispatch(bodyB);
            if (bodyB.physicsComponent) bodyB.physicsComponent.onBeginContact.dispatch(bodyA);
        }
    }

    private handleEndContact(e: p2.EndContactEvent): void
    {
        const bodyA = e.bodyA;
        const bodyB = e.bodyB;

        if (bodyA && bodyB)
        {
            if (bodyA.onEndContact) bodyA.onEndContact(bodyB);
            if (bodyB.onEndContact) bodyB.onEndContact(bodyA);
            if (bodyA.physicsComponent) bodyA.physicsComponent.onEndContact.dispatch(bodyB);
            if (bodyB.physicsComponent) bodyB.physicsComponent.onEndContact.dispatch(bodyA);
        }
    }

    // === UTILITY ====================================================================================================

    public getSecs(): number
    {
        return new Date().getTime() / 1000;
    }

    /**
     * Count number of bodies in the world, more for debugging/profiling
     * @returns Number of bodies currently in the world
     */
    public countBodies(): number
    {
        return this.world.bodies.length;
    }

    /**
     * Count number of shapes in the world, more for debugging/profiling
     * @returns Number of shapes currently in the world
     */
    public countShapes(): number
    {
        let count = 0;

        this.world.bodies.forEach((body) => { count += body.shapes.length; });

        return count;
    }

    public getProfile(): Record<string, any>
    {
        return {
            bodies: this.countBodies(),
            shapes: this.countShapes(),
        };
    }
}

